import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { API_BASE_URL } from "../../../config";
import { asErr } from "../../../utils/apiHelpers";
import { GoogleMapsLoader } from "../../maps/GoogleMapsLoader";
import { isAuthenticated } from "../../../services/authService";
import * as donorService from "../../../services/donorService";
import { getHygieneDdrById } from "../../../services/hygieneDonationService";
import Breadcrumbs, { type Crumb } from "../../../components/navigation/Breadcrumbs";
import NotBenefactorModal from "../../NotBenefactorModal";

type MosqueDonationDetail = {
  id?: number;
  mosqueId?: number;
  mosque_id?: number;
  mosqueName?: string;
  mosque_name?: string;
  mosqueNumber?: string | number;
  request_id?: number;
  request_number?: string | number;
  request_name?: string;
  serialNumber?: string | number;
  number?: string | number;

  classificationName?: string;
  mosqueClassification?: string;
  classification_name?: string | null;

  regionName?: string;
  region_name?: string | null;
  cityName?: string;
  city_name?: string | null;
  centerName?: string;
  moia_center_name?: string | null;
  districtName?: string;
  district_name?: string | null;
  neighborhoodName?: string;
  fullAddress?: string;
  full_address?: string | null;

  capacity?: number | null;
  women_prayer_room_capacity?: number | null;
  hasWomenPrayerRoom?: boolean | string | null;
  hasWomenSection?: boolean | string | null;
  has_women_prayer_room?: string | null;

  cat_a_name?: string | null;
  cat_b_name?: string | null;
  cat_c_name?: string | null;
  cat_c_names?: string | null;
  cat_d_name?: string | null;
  cat_e_names?: string | null;

  statusName?: string;
  status?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type NavState = { fromList?: boolean; backTo?: string; item?: MosqueDonationDetail; source?: "hygiene"; fromMyRequests?: boolean } | null;

const getBaseURL = () => {
  if (process.env.NODE_ENV === "development") return "";
  return API_BASE_URL;
};

const yesNoAr = (v?: string | boolean | null) => {
  if (v === true) return "نعم";
  if (v === false) return "لا";
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "—";
  return ["1", "true", "t", "yes", "y", "نعم", "ok", "✓"].includes(s) ? "نعم" : "لا";
};

const b64urlEncode = (input: string) => {
  const b64 = btoa(unescape(encodeURIComponent(input)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const buildGoogleMapsLink = (lat?: number | null, lng?: number | null) => {
  if (lat == null || lng == null) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&hl=ar&z=16`;
};

const STORAGE_KEY_RETURN = "mosque-donation-details-return";

function loadStoredReturn(id: string | undefined): MosqueDonationDetail | null {
  if (!id || typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_RETURN);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; item?: MosqueDonationDetail };
    if (parsed.id !== id || !parsed.item || typeof parsed.item !== "object") return null;
    sessionStorage.removeItem(STORAGE_KEY_RETURN);
    return parsed.item as MosqueDonationDetail;
  } catch {
    return null;
  }
}

async function fetchDonationMosqueById(id: string): Promise<MosqueDonationDetail> {
  const http = axios.create({
    baseURL: getBaseURL(),
    headers: { Accept: "application/json" },
    withCredentials: false,
  });

  const url = `/api/donations/mosques/opportunities/${encodeURIComponent(id)}`;
  const res = await http.get(url, { validateStatus: (s) => s >= 200 && s < 500 });
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }

  const payload = res.data?.data ?? res.data;
  if (Array.isArray(payload)) return payload[0] as MosqueDonationDetail;
  if (Array.isArray(payload?.items)) return payload.items[0] as MosqueDonationDetail;
  if (Array.isArray(payload?.data)) return payload.data[0] as MosqueDonationDetail;
  return payload as MosqueDonationDetail;
}

export default function MosqueDonationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState) || null;
  const backTo = navState?.backTo || "/mosque/donation-opportunities";
  const isHygiene = navState?.source === "hygiene";
  const fromMyRequests = navState?.fromMyRequests === true;

  const initialItem = navState?.item ?? null;
  const [loading, setLoading] = useState(!initialItem);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<MosqueDonationDetail | null>(initialItem);
  const [showNotBenefactorModal, setShowNotBenefactorModal] = useState(false);
  const [applyChecking, setApplyChecking] = useState(false);
  const restoredFromStorageRef = useRef(false);

  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!navState && id) {
      const stored = loadStoredReturn(id);
      if (stored && typeof stored === "object" && Object.keys(stored).length > 0) {
        setData(stored);
        setLoading(false);
        setErr(null);
        restoredFromStorageRef.current = true;
      }
    }
  }, [id, navState]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!id) {
        setErr("Missing id in URL.");
        setLoading(false);
        return;
      }
      try {
        if (!initialItem && !restoredFromStorageRef.current) setLoading(true);
        setErr(null);
        const payload = isHygiene
          ? await getHygieneDdrById(id)
          : await fetchDonationMosqueById(id);
        if (!alive) return;
        const merged = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : null;
        if (merged && Object.keys(merged).length > 0) {
          setData((prev) => (prev ? { ...prev, ...merged } : (merged as MosqueDonationDetail)));
        }
      } catch (e) {
        if (!alive) return;
        if (!initialItem && !restoredFromStorageRef.current) {
          setErr(asErr(e, "Failed to load details").message);
        }
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, initialItem, isHygiene]);

  useEffect(() => {
    if (!data?.latitude || !data?.longitude) return;
    let map: google.maps.Map | null = null;
    GoogleMapsLoader.load("ar", "SA")
      .then((google) => {
        if (!mapRef.current) return;
        const center = { lat: Number(data.latitude), lng: Number(data.longitude) };
        map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 14,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });
        new google.maps.Marker({ position: center, map });
      })
      .catch(() => {});
    return () => {
      map = null;
    };
  }, [data?.latitude, data?.longitude]);

  const crumbs: Crumb[] = useMemo(
    () => [
      { label: "الرئيسية", to: "/" },
      { label: "الخدمات", to: "/services" },
      { label: "فرص عقود الصيانة", to: "/service/mosque-contribution" },
      { label: "تفاصيل المسجد", to: null },
    ],
    []
  );

  const mapsUrl = buildGoogleMapsLink(data?.latitude, data?.longitude);
  const qrValue = mapsUrl || (typeof window !== "undefined" ? window.location.href : "https://example.com");

  if (loading) {
    return (
      <div className="dga-container py-6" dir="rtl">
        <div className="rounded-2xl border border-neutral-200 p-6">
          <p className="text-neutral-700">جاري التحميل…</p>
        </div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="dga-container py-6" dir="rtl">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
          <div className="mb-4 text-red-700">{err || "تعذر تحميل التفاصيل"}</div>
          <div className="flex justify-end">
            <button
              className="inline-flex items-center rounded-xl border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
              onClick={() => nav(backTo)}
            >
              رجوع
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mosqueName = data.mosque_name || data.mosqueName || "—";
  const mosqueNumber = data.mosqueNumber || data.serialNumber || data.number || "—";
  const contributionSerial =
    data.request_number ??
    data.request_name ??
    (data.request_id != null ? `DDR${data.request_id}` : "—");
  const classification =
    data.classification_name || data.classificationName || data.mosqueClassification || "—";
  const region = data.region_name || data.regionName || "—";
  const city = data.city_name || data.cityName || "—";
  const center = data.moia_center_name || data.centerName || "—";
  const district = data.district_name || data.districtName || data.neighborhoodName || "—";
  const fullAddress = data.full_address || data.fullAddress || "—";
  const womenSection = yesNoAr(
    data.has_women_prayer_room ?? data.hasWomenPrayerRoom ?? data.hasWomenSection
  );

  const categoryTags = isHygiene
    ? [
        { label: "A", value: data.cat_a_name },
        { label: "B", value: data.cat_b_name },
        { label: "C", value: data.cat_c_names ?? data.cat_c_name },
      ]
    : [
        { label: "A", value: data.cat_a_name },
        { label: "B", value: data.cat_b_name },
        { label: "C", value: data.cat_c_name },
        { label: "D", value: data.cat_d_name },
        { label: "E", value: data.cat_e_names },
      ];

  const handleApply = async () => {
    const nextPath = "/mosque/information-donation";
    const nextState = { id, item: data, ...(isHygiene ? { source: "hygiene" as const } : {}) };

    if (!isAuthenticated()) {
      const continueToken = b64urlEncode(JSON.stringify({ path: nextPath, state: nextState }));
      nav(`/benefaction/login?c=${encodeURIComponent(continueToken)}`, { replace: true });
      return;
    }

    setApplyChecking(true);
    try {
      const donors = donorService.scopeDonorsToCurrentUser(await donorService.listMine());
      if (donors && donors.length > 0) {
        nav(nextPath, { state: nextState });
      } else {
        if (id && data) {
          try {
            sessionStorage.setItem(STORAGE_KEY_RETURN, JSON.stringify({ id, item: data }));
          } catch {
            /* ignore */
          }
        }
        setShowNotBenefactorModal(true);
      }
    } catch {
      if (id && data) {
        try {
          sessionStorage.setItem(STORAGE_KEY_RETURN, JSON.stringify({ id, item: data }));
        } catch {
          /* ignore */
        }
      }
      setShowNotBenefactorModal(true);
    } finally {
      setApplyChecking(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16" dir="rtl">
      <Breadcrumbs items={crumbs} />

      {/* Grid: 12 cols on lg+ (match LandDetailPage) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT: Info (8 cols) */}
        <section className="col-span-12 lg:col-span-8 bg-white border border-neutral-200 rounded-2xl shadow-sm">
          <div className="px-4 py-3 border-b border-neutral-200">
            <h2 className="text-base font-semibold text-neutral-900 text-center">معلومات المسجد</h2>
          </div>
          <div className="p-4">
            <dl className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-x-6 gap-y-3 text-sm">
              <dt className="text-neutral-500">اسم المسجد</dt>
              <dd className="text-neutral-900">{mosqueName || "—"}</dd>

              <dt className="text-neutral-500">الرقم التسلسلي للمسجد</dt>
              <dd className="text-neutral-900">{mosqueNumber}</dd>

              <dt className="text-neutral-500">الرقم التسلسلي للمساهمة</dt>
              <dd className="text-neutral-900">{contributionSerial}</dd>

              <dt className="text-neutral-500">التصنيف</dt>
              <dd className="text-neutral-900">{classification}</dd>

              <dt className="text-neutral-500">المنطقة</dt>
              <dd className="text-neutral-900">{region}</dd>

              <dt className="text-neutral-500">المدينة</dt>
              <dd className="text-neutral-900">{city}</dd>

              <dt className="text-neutral-500">المركز</dt>
              <dd className="text-neutral-900">{center}</dd>

              <dt className="text-neutral-500">الحي</dt>
              <dd className="text-neutral-900">{district}</dd>

              <dt className="text-neutral-500">العنوان الكامل</dt>
              <dd className="text-neutral-900">{fullAddress}</dd>

              <dt className="text-neutral-500">قسم النساء</dt>
              <dd className="text-neutral-900">{womenSection}</dd>

              

            </dl>

            <section className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
              <div className="space-y-2">
                {categoryTags.map((tag) => (
                  <div key={tag.label} className="flex items-start gap-2">
                    <span className="shrink-0 inline-flex items-center rounded-full border border-neutral-300 bg-white px-2 py-0.5 text-xs font-semibold text-neutral-800">
                      {tag.label}
                    </span>
                    <span className="text-sm text-neutral-800 leading-relaxed break-words whitespace-normal">
                      {tag.value || "-"}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <div className="my-4 h-px bg-neutral-200" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                className="inline-flex h-12 items-center justify-center rounded-lg border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                onClick={() => nav(backTo)}
              >
                رجوع
              </button>

              {!fromMyRequests && (
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={applyChecking}
                  className="inline-flex h-12 items-center justify-center rounded-lg bg-sa-600 px-4 text-sm font-semibold text-white hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-70"
                >
                  {applyChecking ? "جاري التحقق..." : "تقديم الطلب"}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* RIGHT: Map + QR (4 cols) */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <section className="bg-white border border-neutral-200 rounded-2xl shadow-sm">
            <div className="px-4 py-3 border-b border-neutral-200">
              <h2 className="text-base font-semibold text-neutral-900 text-center">الموقع الجغرافي</h2>
            </div>
            <div className="p-4">
              {data.latitude && data.longitude ? (
                <div ref={mapRef} className="h-72 w-full rounded-xl border border-neutral-200 overflow-hidden" />
              ) : (
                <div className="h-72 w-full rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center text-neutral-500">
                  الموقع غير متوفر
                </div>
              )}
            </div>
          </section>

          <section className="bg-white border border-neutral-200 rounded-2xl shadow-sm">
            <div className="px-4 py-3 border-b border-neutral-200">
              <h2 className="text-base font-semibold text-neutral-900 text-center">باركود</h2>
            </div>
            <div className="p-4 flex items-center justify-center">
              <QRCodeCanvas value={qrValue} size={140} includeMargin />
            </div>
          </section>
        </div>
      </div>
      <NotBenefactorModal open={showNotBenefactorModal} onClose={() => setShowNotBenefactorModal(false)} isLoggedIn={true} />
    </div>
  );
}
